import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('personal');
export interface PersonalInfo {
  id: string;
  taskId: number;
  feet: number;
  inches: number;
  weight: number;
  eyeColor: string;
  hairColor: string;
  bloodType: string;
  identifyingMarks: string;
  isLocked: boolean;
}

class PersonalInfoService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getPersonalInfo(taskId: number, targetUserId?: string): Promise<PersonalInfo[]> {
    const params = new URLSearchParams();
    params.append('taskId', taskId.toString());
    if (targetUserId) {
      params.append('targetUserId', targetUserId);
    }
    const response = await this.makeRequest(`/api/personal-info?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch personal info: ${response.status}`);
    }

    const result = await response.json();
    return result.success ? result.data : [];
  }

  async createPersonalInfo(data: any): Promise<PersonalInfo> {
    const response = await this.makeRequest('/api/personal-info', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to create personal info: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  async updatePersonalInfo(id: string, data: any): Promise<PersonalInfo> {
    const response = await this.makeRequest(`/api/personal-info/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to update personal info: ${response.status}`);
    }

    const result = await response.json();
    return result.data;
  }

  async deletePersonalInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/personal-info/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to delete personal info: ${response.status}`);
    }
  }

  async lockPersonalInfo(id: string): Promise<void> {
    const response = await this.makeRequest(`/api/personal-info/${id}/lock`, {
      method: 'POST',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to lock personal info: ${response.status}`);
    }
  }

  async unlockPersonalInfo(id: string, emails: string[]): Promise<void> {
    const response = await this.makeRequest(`/api/personal-info/${id}/unlock`, {
      method: 'POST',
      body: JSON.stringify({ emails }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to unlock personal info: ${response.status}`);
    }
  }

  async getPersonalInfoAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await this.makeRequest(`/api/personal-info/${id}/access-grants`);

      if (!response.ok) {
        throw new Error(`Failed to get personal info access grants: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) {
      logger.error('Error fetching personal info access grants:', error);
      throw error;
    }
  }
}

export const personalInfoService = new PersonalInfoService();